// [檔案： src/js/calendar.js]
// 包含了 flatpickr 手機版修復 + 滑動轉場動畫 + 變數範圍修復 + 月份選擇器同步修復(重建實例法)

import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index.js';
import { dom } from './dom.js';
import * as state from './state.js';
import { loadTransactions } from './transactions.js';
import { formatMonthDisplay } from './utils.js';
import { currentLang } from './i18n.js';
import { getFlatpickrLocale } from './locale.js';

// --- 動畫狀態鎖 ---
let isTransitioning = false; 
const ANIMATION_DURATION = 250; 

// --- 建立 Date Picker 的輔助函式 ---
async function createDatePicker(startDate) {
    const locale = await getFlatpickrLocale(currentLang);
    
    // 如果舊實例存在，先銷毀 (確保乾淨)
    if (state.monthSelectInstance) {
        state.monthSelectInstance.destroy();
        state.setMonthSelectInstance(null);
    }

    const msInstanceResult = flatpickr(dom.monthPickerInput, { 
        plugins: [
            new monthSelectPlugin({
                shorthand: false, 
                dateFormat: "Y-m", 
                theme: state.currentThemeIsDark ? "dark" : "light",
                maxDate: new Date()
            })
        ],
        disableMobile: true, // 強制在手機上也使用 JS 介面
        appendTo: document.body, 
        positionElement: dom.currentMonthDisplay, 
        locale: locale,
        defaultDate: startDate, // 每次建立時，這裡就是最新的日期
        maxDate: new Date(), 
        onChange: (selectedDates, dateStr, instance) => {
            if (selectedDates[0]) {
                state.setCurrentDate(selectedDates[0]);
                // 因為是 Picker 自己觸發的，所以傳入 true 跳過重建
                updateMonthDisplay(true); 
                loadTransactions(); 
            }
        },
    });

    let finalInstance = null;
    if (Array.isArray(msInstanceResult)) {
        finalInstance = msInstanceResult[0]; 
    } else {
        finalInstance = msInstanceResult;
    }

    if (finalInstance) {
        state.setMonthSelectInstance(finalInstance);
    }
}

/**
 * 更新月份顯示和導覽按鈕
 * @param {boolean} skipReinit - 是否跳過重建 DatePicker (若由 Picker 自身觸發則設為 true)
 */
export async function updateMonthDisplay(skipReinit = false) {
    
    dom.currentMonthDisplay.textContent = formatMonthDisplay(state.currentDate);
    
    // 如果不是由 Picker 觸發的更新 (例如箭頭或手勢)，
    // 則銷毀並重建 Picker，確保它與當前月份 100% 同步
    if (!skipReinit) {
        await createDatePicker(state.currentDate);
    }
    
    updateMonthNavigation();
}

/**
 * 檢查並更新月份導覽按鈕的狀態 (是否為未來月份)
 */
export function updateMonthNavigation() {
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const appDate = new Date(state.currentDate.getFullYear(), state.currentDate.getMonth(), 1);

    if (appDate >= currentMonth) {
        dom.nextMonthBtn.disabled = true;
        dom.nextMonthBtn.classList.add('opacity-50', 'cursor-not-allowed');
    } else {
        dom.nextMonthBtn.disabled = false;
        dom.nextMonthBtn.classList.remove('opacity-50', 'cursor-not-allowed');
    }
}

/**
 * 初始化所有 Flatpickr 實例
 */
export async function initializeCalendars() {
    
    const mainElement = document.querySelector('main');
    if (!mainElement) {
        console.error("關鍵錯誤：在 initializeCalendars 中找不到 <main> 元素！");
    }

    // 1. 首次建立 DatePicker
    await createDatePicker(state.currentDate);

    // 2. 綁定左右箭頭事件 (含動畫)
    if (!state.monthNavInitialized) {
        // 修正：將背景改為透明，避免動畫時遮蓋底層通透背景
        const mainClasses = 'absolute inset-0 flex flex-col overflow-hidden bg-transparent';
        
        // --- 「上個月」按鈕 ---
        dom.prevMonthBtn.addEventListener('click', () => {
            if (isTransitioning) return;
            if (!mainElement) return;
            
            isTransitioning = true; 

            mainElement.className = `${mainClasses} page-transition slide-out-right`;
            
            setTimeout(() => {
                state.currentDate.setMonth(state.currentDate.getMonth() - 1);
                updateMonthDisplay(); // 這裡會觸發重建 Picker
                loadTransactions();

                mainElement.className = `${mainClasses} page-transition slide-in-from-left`;
                
                setTimeout(() => {
                    mainElement.className = mainClasses;
                    isTransitioning = false;
                }, ANIMATION_DURATION);

            }, ANIMATION_DURATION);
        });
        
        // --- 「下個月」按鈕 ---
        dom.nextMonthBtn.addEventListener('click', () => {
            if (isTransitioning) return;
            if (dom.nextMonthBtn.disabled) return;
            if (!mainElement) return;
            
            isTransitioning = true; 

            mainElement.className = `${mainClasses} page-transition slide-out-left`;

            setTimeout(() => {
                state.currentDate.setMonth(state.currentDate.getMonth() + 1);
                updateMonthDisplay(); // 這裡會觸發重建 Picker
                loadTransactions(); 

                mainElement.className = `${mainClasses} page-transition slide-in-from-right`;

                setTimeout(() => {
                    mainElement.className = mainClasses;
                    isTransitioning = false;
                }, ANIMATION_DURATION);

            }, ANIMATION_DURATION);
        });
        
        state.setMonthNavInitialized(true);
    }

    // (首次執行更新文字)
    updateMonthDisplay(true); // 首次建立已經在上面做過了，這裡只更新文字，所以 skipReinit=true
}